/**
 * Home — the greeting, the dog hero, what is coming up, and today's feeding routine.
 *
 * WHAT CHANGED FROM THE APPROVED PROTOTYPE, AND WHY
 * ------------------------------------------------
 * The prototype rendered one complete sample dog, so every card always had something in it. Against
 * the 199 live records almost nothing is guaranteed: the care dates were only added to the form on
 * 2026-07-30 and are empty for ~100% of dogs, and a meal record is only confidently linked for a
 * minority. So every block on this screen has a designed empty state, and NONE of them is a blank
 * card — an absence a client cannot act on is worse than no card at all.
 *
 * Three numbers the prototype drew are gone rather than reproduced, because nothing on a real record
 * backs them: the 92% "profile complete" ring, the grooming item at an invented 64%, and the
 * hard-coded "30 Sep — contact check" row. Where a ring still appears it is drawing
 * `CareItem.percent`, which the SERVER computed from real dates.
 *
 * NOTHING HERE RECOMPUTES A DUE DATE. `dog.care` arrives already derived; `comingUp()` from
 * @ft/shared only sorts and caps it, which is the single reason this screen and the Reminders screen
 * provably cannot disagree. If you ever find yourself reaching for `addEvery` in this file, stop.
 */

import { useId, useRef, useState, type CSSProperties, type ReactElement } from 'react'
import {
  CANNOT_CLEAR_FOOD_DETAILS_CUSTOMER_MESSAGE,
  CANNOT_REMOVE_SELECTION_CUSTOMER_MESSAGE,
  CUSTOMER_EDITABLE_MEAL_QIDS,
  MEAL_FORM,
  comingUp,
  fmtDay,
  parseDay,
} from '@ft/shared'
import type { CareItem, DogPayload, FoodEditValue, FoodPlan, RecordDocument } from '@ft/shared'

import { editFood, getFoodEditValues } from '../api/client'
import { MealFieldEditor } from '../components/MealFieldEditor'
import { CARE_ICON_PATHS, careIconFor, Icon, UI_ICON_PATHS, BrandPaw } from '../model/icons'
import { PHONE_DISPLAY, PHONE_HREF } from '../model/contact'
import { useMe, usePortal, useSelectedDog } from '../state/portal'

// ── Style recipes ───────────────────────────────────────────────────────────────────────────────
//
// Every value is a STRING. React never appends a unit to a number it is given for an unknown
// property, and the design-tool runtime this was ported from only ever produced strings — writing
// `width: 8` here renders `8px` in some places and nothing usable in others.

/**
 * The prototype's header carried a 46px initials disc on the right. It is gone: the shell's account
 * row already shows who is signed in, two identity markers 40px apart read as an unfinished port,
 * and Organic asks for exactly this — flush-left headings with the whitespace on the right.
 */
const HEADER_ROW: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '14px',
  marginBottom: '24px',
}

const KICKER: CSSProperties = {
  fontSize: '12px',
  fontWeight: '700',
  letterSpacing: '.09em',
  textTransform: 'uppercase',
  color: 'var(--color-accent-700)',
  marginBottom: '7px',
}

const PAGE_TITLE: CSSProperties = { margin: '0 0 7px', fontSize: 'clamp(30px, 5.5vw, 42px)' }
const LEAD: CSSProperties = { margin: '0', fontSize: '15px' }

const HERO: CSSProperties = {
  position: 'relative',
  overflow: 'hidden',
  background: 'var(--color-accent-2-200)',
  borderRadius: 'calc(var(--radius-lg) * 1.15)',
  padding: '24px',
  boxShadow: 'var(--shadow-sm)',
}

const BLOB_A: CSSProperties = {
  position: 'absolute',
  width: '240px',
  height: '240px',
  borderRadius: '50%',
  background: 'var(--color-accent-2-300)',
  opacity: '.45',
  right: '-85px',
  top: '-110px',
}

const BLOB_B: CSSProperties = {
  position: 'absolute',
  width: '90px',
  height: '90px',
  borderRadius: '50%',
  background: 'var(--color-accent-2-100)',
  opacity: '.6',
  left: '-30px',
  bottom: '-40px',
}

const HERO_ROW: CSSProperties = {
  position: 'relative',
  display: 'flex',
  gap: '16px',
  alignItems: 'center',
  marginBottom: '24px',
}

/**
 * The prototype cropped at `object-position:50% 62%`, which was tuned to one specific photograph of
 * one specific dog. Applied to arbitrary client uploads that crop cuts heads off, so this centres.
 *
 * Sized to hold its own against a 31px dog name beside it — an 88px circle read as an avatar next
 * to the heading rather than as the dog. It stays a CIRCLE here, unlike the portrait on the profile
 * screen: this one sits inside the hero card as part of a row, where a rounded square would fight
 * the card's own corners a few pixels away.
 */
const HERO_PHOTO: CSSProperties = {
  width: 'clamp(104px, 17vw, 132px)',
  aspectRatio: '1',
  flex: 'none',
  borderRadius: '50%',
  objectFit: 'cover',
  objectPosition: '50% 50%',
  border: '4px solid var(--color-bg)',
  boxShadow: 'var(--shadow-md)',
}

const HERO_PHOTO_FALLBACK: CSSProperties = {
  ...HERO_PHOTO,
  display: 'grid',
  placeItems: 'center',
  background: 'var(--color-accent-2-100)',
  color: 'var(--color-accent-2-700)',
}

const DOG_NAME: CSSProperties = {
  fontFamily: 'var(--font-heading)',
  fontSize: '31px',
  lineHeight: '1.05',
}

const HERO_LINE: CSSProperties = {
  color: 'var(--color-accent-2-800)',
  fontSize: '14px',
  marginTop: '3px',
}

const HERO_PILL: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '7px',
  marginTop: '10px',
  padding: '5px 12px',
  borderRadius: '999px',
  background: 'var(--color-bg)',
  fontSize: '12px',
  fontWeight: '700',
}

const HERO_FOOT: CSSProperties = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '14px',
  flexWrap: 'wrap',
}

const RING_WRAP: CSSProperties = {
  position: 'relative',
  width: '62px',
  height: '62px',
  flex: 'none',
  display: 'grid',
  placeItems: 'center',
}

const HERO_CAPTION: CSSProperties = { fontSize: '12.5px', color: 'var(--color-accent-2-800)' }
const HERO_CAPTION_STRONG: CSSProperties = {
  display: 'block',
  fontSize: '14.5px',
  color: 'var(--color-text)',
}

const SECTION_HEAD: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: '12px',
  margin: '30px 4px 12px',
}

const SECTION_TITLE: CSSProperties = { margin: '0', fontSize: '22px' }
const SECTION_SUB: CSSProperties = { fontSize: '12.5px' }

const CARE_GRID: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(155px, 1fr))',
  gap: '12px',
}

const CARE_CARD: CSSProperties = { gap: '12px', padding: '16px' }
const CARE_CARD_TOP: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '8px',
}

const CARE_LABEL: CSSProperties = { fontSize: '12.5px' }
const CARE_WHEN: CSSProperties = { fontWeight: '700', fontSize: '15.5px', marginTop: '2px' }

/**
 * The prototype's meal row was `52px 1fr auto`, with a chevron in the third column and no click
 * handler anywhere on the row. A chevron promises a detail view; this portal is read-only and has
 * none, so the column goes with it rather than shipping an affordance that does nothing.
 */
const MEAL_ROW: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '52px 1fr',
  gap: '12px',
  alignItems: 'center',
  padding: '13px 10px',
}

const MEAL_TITLE: CSSProperties = { display: 'block', fontWeight: '700', fontSize: '14.5px' }
const MEAL_SUB: CSSProperties = { display: 'block', fontSize: '12px', marginTop: '2px' }

const EMPTY_CARD: CSSProperties = {
  alignItems: 'center',
  textAlign: 'center',
  gap: '0',
  padding: '36px 26px',
}

const EMPTY_DISC: CSSProperties = {
  width: '56px',
  height: '56px',
  borderRadius: '50%',
  display: 'grid',
  placeItems: 'center',
  background: 'var(--color-neutral-200)',
  color: 'var(--color-neutral-700)',
  marginBottom: '14px',
}

const EMPTY_TITLE: CSSProperties = { margin: '0 0 6px', fontSize: '21px' }
const EMPTY_BODY: CSSProperties = { maxWidth: '40ch', margin: '0', fontSize: '14px' }

/**
 * A .btn-ghost computes to about 34px tall, under the 44px tap floor. The prototype left the ghost
 * buttons on this screen unsized; on a phone that is the difference between a tap and a mis-tap.
 */
const TAP_TARGET: CSSProperties = { minHeight: '44px' }

// ── Presentation helpers ────────────────────────────────────────────────────────────────────────
//
// Formatting only. Anything that decides WHEN something is due lives in @ft/shared and runs on the
// server; two copies of that would drift, and the drift would surface as one client being told a
// different date on two screens of the same app.

/** The reader's own clock, not the server's — "good evening" is about where they are sitting. */
function greetingFor(hour: number): string {
  if (hour < 12) return 'Good morning'
  if (hour < 18) return 'Good afternoon'
  return 'Good evening'
}

/**
 * The date line comes from `MePayload.serverDate` so a phone with a wrong clock cannot put the wrong
 * day at the top of the page. Returns '' rather than 'Invalid Date' when the payload surprises us.
 */
function dateLineFor(serverDate: string): string {
  const day = parseDay(serverDate)
  if (!day) return ''
  return day.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })
}

/** 'there' is the fallback the prototype used, and it is still the right one: no name, no blank. */
function firstNameOf(name: string | null): string {
  const first = (name ?? '').trim().split(/\s+/)[0]
  return first === undefined || first === '' ? 'there' : first
}

/**
 * Organic carries no red/amber/green ramp. Urgency reads as terracotta accent (needs attention)
 * against sage accent-2 (fine) — the same idiom the whole portal uses.
 */
function tagClassFor(item: CareItem): string {
  return item.urgency === 'attention' ? 'tag tag-accent' : 'tag tag-accent-2'
}

function discColoursFor(item: CareItem): CSSProperties {
  return item.urgency === 'attention'
    ? { background: 'var(--color-accent-100)', color: 'var(--color-accent-700)' }
    : { background: 'var(--color-accent-2-100)', color: 'var(--color-accent-2-700)' }
}

/** The prototype's ring: a 163.4-unit dash, offset by the fraction of the cycle still to run. */
function ringOffsetFor(percent: number): string {
  return (163.4 * (1 - percent / 100)).toFixed(1)
}

/**
 * The client's payload guard is deliberately shallow — it checks the properties whose absence would
 * render as a plausible lie, not every leaf — so `documents` arrives unvalidated. Reading it
 * defensively is the difference between a missing avatar and a render throw that takes the whole
 * screen out for a customer.
 */
function photoOf(documents: readonly RecordDocument[] | undefined): RecordDocument | null {
  if (!Array.isArray(documents)) return null
  return documents.find((doc) => doc.kind === 'photo') ?? null
}

// ── Shared empty state ──────────────────────────────────────────────────────────────────────────

interface EmptyStateProps {
  readonly paths: readonly string[]
  readonly title: string
  readonly body: string
  /**
   * The only affordance a read-only portal can honestly offer. Deliberately not a link or a button:
   * there is no in-app way to send a correction in v1, and a dead control is worse than a sentence.
   */
  readonly hint?: string
}

function EmptyState({ paths, title, body, hint }: EmptyStateProps): ReactElement {
  return (
    <>
      <span style={EMPTY_DISC}>
        <Icon paths={paths} size={24} />
      </span>
      <h4 style={EMPTY_TITLE}>{title}</h4>
      <p className="text-muted" style={EMPTY_BODY}>
        {body}
      </p>
      {hint === undefined ? null : (
        <span className="tag tag-neutral" style={{ marginTop: '16px', fontWeight: '700' }}>
          {hint}
        </span>
      )}
    </>
  )
}

// ── The hero ────────────────────────────────────────────────────────────────────────────────────

interface HeroProps {
  readonly dog: DogPayload
  readonly onReview: () => void
}

function Hero({ dog, onReview }: HeroProps): ReactElement {
  // A photo served by the authenticated proxy can 404 (the file was removed at source, or the
  // record links an id the proxy will not release). Falling back to the mark is better than the
  // browser's broken-image glyph in a client's face. Keyed by URL so switching dogs re-tries.
  const [brokenPhotoUrl, setBrokenPhotoUrl] = useState<string | null>(null)
  const photo = photoOf(dog.record?.documents)
  const showPhoto = photo !== null && photo.url !== brokenPhotoUrl
  // Breed, date of birth and weight are all optional on the live form, so the server can honestly
  // assemble nothing at all. An empty line under the name reads as a layout bug, not as a gap.
  const heroLine = (dog.heroLine ?? '').trim()

  const attention = dog.care.filter((item) => item.urgency === 'attention').length
  // The review item is the only care entry that is about the RECORD rather than about a treatment,
  // and `comingUp()` filters it out of the cards below — so showing it here duplicates nothing.
  const review = dog.care.find((item) => item.kind === 'contact') ?? null
  const reviewNeedsAttention = review !== null && review.urgency === 'attention'

  return (
    <article style={HERO}>
      <span aria-hidden="true" style={BLOB_A} />
      <span aria-hidden="true" style={BLOB_B} />

      <div style={HERO_ROW}>
        {showPhoto && photo !== null ? (
          // Decorative: the heading two inches to the right already says the dog's name, so a
          // screen reader announcing it again is noise.
          <img
            className="washed"
            src={photo.url}
            alt=""
            style={HERO_PHOTO}
            onError={() => setBrokenPhotoUrl(photo.url)}
          />
        ) : (
          <span aria-hidden="true" style={HERO_PHOTO_FALLBACK}>
            {/* Scaled with the circle. At 34px it sat as a dot in the middle of the larger mount. */}
            <BrandPaw size={46} />
          </span>
        )}

        <div>
          <div style={DOG_NAME}>{dog.name}</div>
          {/* Assembled server-side, and already honest about a weight it could not parse. */}
          <div style={HERO_LINE}>
            {heroLine === '' ? `We don’t have ${dog.name}’s breed, age or weight yet` : heroLine}
          </div>

          {dog.care.length === 0 ? null : (
            <span style={HERO_PILL}>
              <span
                aria-hidden="true"
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: attention > 0 ? 'var(--color-accent)' : 'var(--color-accent-2-500)',
                }}
              />
              {attention === 0
                ? 'Everything up to date'
                : attention === 1
                  ? '1 thing needs attention'
                  : `${attention} things need attention`}
            </span>
          )}
        </div>
      </div>

      <div style={HERO_FOOT}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {review !== null && review.percent !== null ? (
            <span style={RING_WRAP}>
              <svg
                width="62"
                height="62"
                viewBox="0 0 64 64"
                style={{ position: 'absolute', inset: '0' }}
                aria-hidden="true"
                focusable="false"
              >
                <circle cx="32" cy="32" r="26" fill="none" stroke="var(--color-bg)" strokeWidth="7" />
                <circle
                  cx="32"
                  cy="32"
                  r="26"
                  fill="none"
                  stroke="var(--color-accent)"
                  strokeWidth="7"
                  strokeLinecap="round"
                  strokeDasharray="163.4"
                  strokeDashoffset={ringOffsetFor(review.percent)}
                  transform="rotate(-90 32 32)"
                />
              </svg>
              <strong style={{ fontSize: '13.5px', letterSpacing: '-.02em' }}>
                {review.percent}%
              </strong>
            </span>
          ) : (
            <span
              aria-hidden="true"
              style={{
                ...RING_WRAP,
                borderRadius: '50%',
                background: 'var(--color-bg)',
                color: 'var(--color-accent-700)',
              }}
            >
              <Icon paths={UI_ICON_PATHS.message} size={24} />
            </span>
          )}

          <div style={HERO_CAPTION}>
            {review === null ? (
              <>
                <strong style={HERO_CAPTION_STRONG}>Nothing to confirm yet</strong>
                have a look through and tell us if anything is wrong
              </>
            ) : (
              <>
                <strong style={HERO_CAPTION_STRONG}>Details check</strong>
                {review.detail}
              </>
            )}
          </div>
        </div>

        {/* Primary only when the record actually needs the client's attention — otherwise this is a
            quiet, always-available way in, not a nag. */}
        {reviewNeedsAttention ? (
          <button type="button" className="btn btn-primary" onClick={onReview} style={TAP_TARGET}>
            Check details
            <Icon paths={UI_ICON_PATHS.arrowRight} size={15} />
          </button>
        ) : (
          <button type="button" className="btn btn-secondary" onClick={onReview} style={TAP_TARGET}>
            Review details
          </button>
        )}
      </div>
    </article>
  )
}

// ── Coming up ───────────────────────────────────────────────────────────────────────────────────

interface ComingUpProps {
  readonly dog: DogPayload
  readonly onSeeAll: () => void
}

function ComingUp({ dog, onSeeAll }: ComingUpProps): ReactElement {
  // The SAME array the Reminders screen renders, only sorted and capped. Recomputing a schedule here
  // is the one change that could make the front page contradict the rest of the portal.
  const upcoming = comingUp(dog.care, 4)

  return (
    <>
      <div style={SECTION_HEAD}>
        <div>
          <h3 style={SECTION_TITLE}>Coming up</h3>
          <span className="text-muted" style={SECTION_SUB}>
            {`Based on ${dog.name}’s schedule`}
          </span>
        </div>
        {upcoming.length === 0 ? null : (
          <button type="button" className="btn btn-ghost" onClick={onSeeAll} style={TAP_TARGET}>
            See all
          </button>
        )}
      </div>

      {upcoming.length === 0 ? (
        <section className="card elev-sm" style={EMPTY_CARD}>
          <EmptyState
            paths={CARE_ICON_PATHS.shield}
            title="No treatment dates yet"
            body={
              dog.careEmpty
                ? `We don’t have any of ${dog.name}’s care dates on file — worming, flea, annual booster or kennel cough. Once we do, this is where the next one shows up.`
                : `None of ${dog.name}’s treatments has a date on file yet. Once one does, this is where the next one shows up.`
            }
            hint="Bring the dates to your next visit"
          />
        </section>
      ) : (
        <div style={CARE_GRID}>
          {upcoming.map((item) => (
            <article key={item.kind} className="card elev-sm" style={CARE_CARD}>
              <div style={CARE_CARD_TOP}>
                <span
                  style={{
                    width: '42px',
                    height: '42px',
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    ...discColoursFor(item),
                  }}
                >
                  <Icon paths={careIconFor(item.kind)} size={19} />
                </span>
                <span className={tagClassFor(item)} style={{ fontWeight: '700' }}>
                  {item.status}
                </span>
              </div>
              <div>
                <div className="text-muted" style={CARE_LABEL}>
                  {item.name}
                </div>
                {/* `needsInterval` items have a last-given date but no workable interval, so there
                    genuinely is no next date to print. The tag above already asks for one. */}
                <div style={CARE_WHEN}>{item.due === null ? 'No date yet' : fmtDay(item.due)}</div>
              </div>
            </article>
          ))}
        </div>
      )}
    </>
  )
}

// ── Today ───────────────────────────────────────────────────────────────────────────────────────

function MealRows({ plan, dogName }: { readonly plan: FoodPlan; readonly dogName: string }): ReactElement {
  const mealtimeCareLine = [plan.supplementLine, plan.medicationLine].filter(Boolean).join(' · ')

  return (
    <>
      {plan.portionUnknown ? null : (
        <div
          style={{
            ...MEAL_ROW,
            borderBottom: '1px solid var(--color-divider)',
          }}
        >
          <span className="text-muted" style={MEAL_SUB}>
            Daily amount
          </span>
          <span>
            <span style={MEAL_TITLE}>{plan.dailyPortion}</span>
            <span className="text-muted" style={MEAL_SUB}>
              {plan.typeLine}
            </span>
          </span>
        </div>
      )}

      <div
        style={{
          ...MEAL_ROW,
          borderBottom: '1px solid var(--color-divider)',
        }}
      >
        <span className="text-muted" style={MEAL_SUB}>
          How often
        </span>
        <span>
          <span style={MEAL_TITLE}>{plan.mealsLine}</span>
          {mealtimeCareLine === '' ? null : (
            <span className="text-muted" style={MEAL_SUB}>
              {mealtimeCareLine}
            </span>
          )}
        </span>
      </div>

      <div
        style={{
          ...MEAL_ROW,
          // The last row's rule would draw against the card's own edge.
          borderBottom:
            plan.notes === null && !plan.portionUnknown
              ? undefined
              : '1px solid var(--color-divider)',
        }}
      >
        <span className="text-muted" style={MEAL_SUB}>
          Which meals
        </span>
        <span style={MEAL_TITLE}>{plan.slots.map((slot) => slot.name).join(' · ')}</span>
      </div>

      {/* An unrecorded portion is a real gap the team hits at the bowl, so it is said out loud
          rather than left implicit. */}
      {plan.portionUnknown ? (
        <p
          className="text-muted"
          style={{
            margin: '0',
            padding: '13px 10px',
            fontSize: '12.5px',
            borderBottom: plan.notes === null ? undefined : '1px solid var(--color-divider)',
          }}
        >
          {`We don’t have ${dogName}’s portion sizes — tell us at your next visit and we’ll add them.`}
        </p>
      ) : null}

      {plan.notes === null ? null : (
        <p style={{ margin: '0', padding: '13px 10px', fontSize: '12.5px' }}>
          <strong style={{ display: 'block', fontSize: '12px', letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--color-accent-700)', marginBottom: '3px' }}>
            Note
          </strong>
          {plan.notes}
        </p>
      )}
    </>
  )
}

const CUSTOMER_MEAL_FIELDS = MEAL_FORM.fields.filter((field) =>
  CUSTOMER_EDITABLE_MEAL_QIDS.some((qid) => qid === field.qid),
)

type CustomerMealValues = Record<string, FoodEditValue>

function cloneMealValues(values: Readonly<Record<string, FoodEditValue>>): CustomerMealValues {
  return Object.fromEntries(
    Object.entries(values).map(([qid, value]) => [qid, Array.isArray(value) ? [...value] : value]),
  )
}

function sameMealValue(left: FoodEditValue, right: FoodEditValue): boolean {
  if (typeof left === 'string' || typeof right === 'string') return left === right
  return left.length === right.length && left.every((value, index) => value === right[index])
}

/** Customer-only affordance. Staff mount this screen too, but their cookie cannot use these routes. */
function CustomerFoodEditor({ dogId }: { readonly dogId: string }): ReactElement | null {
  const { applyFood, showToast, viewingAs } = usePortal()
  const panelId = `food-edit-${useId()}`
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loaded, setLoaded] = useState<CustomerMealValues | null>(null)
  const [values, setValues] = useState<CustomerMealValues | null>(null)
  const [alert, setAlert] = useState<string | null>(null)
  const savingRef = useRef(false)

  if (viewingAs !== null) return null

  const toggle = async (): Promise<void> => {
    if (open) {
      setOpen(false)
      setAlert(null)
      return
    }
    if (loading) return
    setLoading(true)
    setAlert(null)
    const result = await getFoodEditValues(dogId)
    setLoading(false)
    if (!result.ok) {
      setAlert("We couldn't load the food details — please try again.")
      return
    }
    const initial = cloneMealValues(result.data.values)
    setLoaded(initial)
    setValues(cloneMealValues(initial))
    setOpen(true)
  }

  const changeValue = (qid: string, next: string | string[]): void => {
    if (!loaded || !values) return
    const original = loaded[qid]
    if (
      Array.isArray(original) &&
      Array.isArray(next) &&
      original.some((option) => !next.includes(option))
    ) {
      setAlert(CANNOT_REMOVE_SELECTION_CUSTOMER_MESSAGE)
      return
    }
    setAlert(null)
    setValues({ ...values, [qid]: next })
  }

  const save = async (): Promise<void> => {
    if (savingRef.current || !loaded || !values) return
    const changes: CustomerMealValues = {}
    for (const qid of CUSTOMER_EDITABLE_MEAL_QIDS) {
      const before = loaded[qid]
      const after = values[qid]
      if (before !== undefined && after !== undefined && !sameMealValue(before, after)) changes[qid] = after
    }
    if (Object.keys(changes).length === 0) {
      setAlert(null)
      return
    }
    if (Object.values(changes).some((value) => value === '' || (Array.isArray(value) && value.length === 0))) {
      setAlert(CANNOT_CLEAR_FOOD_DETAILS_CUSTOMER_MESSAGE)
      return
    }

    savingRef.current = true
    setSaving(true)
    setAlert(null)
    const result = await editFood({ dogId, changes })
    savingRef.current = false
    setSaving(false)
    if (!result.ok) {
      setAlert(result.error.message)
      return
    }
    applyFood(dogId, result.data.food)
    showToast('Food details saved.')
    setOpen(false)
    setLoaded(null)
    setValues(null)
  }

  return (
    <div style={{ padding: '7px 10px 0' }}>
      <button
        type="button"
        className="btn btn-ghost"
        style={TAP_TARGET}
        aria-expanded={open}
        aria-controls={panelId}
        aria-busy={loading}
        onClick={() => void toggle()}
      >
        Edit food details
      </button>
      {alert === null ? null : (
        <p role="alert" style={{ margin: '8px 0 0', fontSize: '13px' }}>{alert}</p>
      )}
      {open && values && loaded ? (
        <div id={panelId} style={{ display: 'grid', gap: '14px', padding: '14px 0 8px' }}>
          <MealFieldEditor fields={CUSTOMER_MEAL_FIELDS} values={values} onChange={changeValue} />
          <button
            type="button"
            className="btn btn-primary"
            style={TAP_TARGET}
            aria-busy={saving}
            onClick={() => void save()}
          >
            Save food details
          </button>
        </div>
      ) : null}
    </div>
  )
}

function Today({ dog }: { readonly dog: DogPayload }): ReactElement {
  const food = dog.food

  return (
    <>
      {/* .ft-today-head is applied in the approved prototype and defined nowhere — not in its own
          <style> block, not in the design system. Carried as a no-op hook; do not invent a rule. */}
      <div style={SECTION_HEAD} className="ft-today-head">
        <div>
          <h3 style={SECTION_TITLE}>Today</h3>
          <span className="text-muted" style={SECTION_SUB}>
            {`${dog.name}’s routine`}
          </span>
        </div>
      </div>

      {food.state === 'available' ? (
        <div className="card elev-sm" style={{ gap: '0', padding: '6px 8px' }}>
          <MealRows plan={food.plan} dogName={dog.name} />
          {food.ownerEdited === undefined ? null : (
            <p style={{ margin: '0', padding: '13px 10px 0', fontSize: '12.5px', fontWeight: '700' }}>
              Changed by owner on {fmtDay(parseDay(food.ownerEdited.on))}.
            </p>
          )}
          {food.linkMethod === 'Human-confirmed' ? <CustomerFoodEditor dogId={dog.dogId} /> : null}
          <p className="text-muted" style={{ margin: '0', padding: '13px 10px', fontSize: '12.5px' }}>
            To remove a food or supplement, or for anything else, call The Fairy Tails on{' '}
            <a href={PHONE_HREF}>{PHONE_DISPLAY}</a>.
          </p>
        </div>
      ) : (
        <section className="card elev-sm" style={EMPTY_CARD}>
          {food.state === 'unconfirmed' ? (
            /* NEVER render a plan in this state. Dog name + parent surname resolves only 62% of
               meal rows and 45% of them name a dog the registration form has never heard of — so a
               best guess here is how one client ends up reading another dog's feeding instructions.
               The server withholds the plan for exactly that reason; do not go looking for it. */
            <EmptyState
              paths={CARE_ICON_PATHS.bone}
              title="We need to check one thing"
              body={`We have a feeding plan on file that may be ${dog.name}’s, but we haven’t confirmed it yet — so we’re not showing it rather than risk showing you another dog’s.`}
              hint="We’ll confirm it with you at your next visit"
            />
          ) : (
            <EmptyState
              paths={CARE_ICON_PATHS.bone}
              title="No feeding plan yet"
              body={`We don’t have ${dog.name}’s feeding plan yet — what they eat, how much, and how many meals a day.`}
              hint="Tell us at your next visit"
            />
          )}
        </section>
      )}
    </>
  )
}

// ── The screen ──────────────────────────────────────────────────────────────────────────────────

/** Every branch renders this wrapper, so the entry animation and the screen label never vary. */
function Screen({ children }: { readonly children: ReactElement }): ReactElement {
  return (
    <section data-screen-label="Home" style={{ animation: 'ft-rise .4s ease both' }}>
      {children}
    </section>
  )
}

export function HomeScreen(): ReactElement {
  const { go } = usePortal()
  const me = useMe()
  const dog = useSelectedDog()

  // First load. `me.data` is null here for BOTH "still fetching" and "the fetch failed", which is
  // why the two are distinguished by `status` and never by a null check on the data.
  if (me.status === 'loading') {
    return (
      <Screen>
        <div className="card elev-sm" style={{ padding: '24px' }}>
          <p className="text-muted" style={{ margin: '0', fontSize: '14px' }}>
            Loading your record…
          </p>
        </div>
      </Screen>
    )
  }

  if (me.status === 'error') {
    return (
      <Screen>
        <section className="card elev-sm" style={EMPTY_CARD}>
          <EmptyState
            paths={UI_ICON_PATHS.close}
            title="We couldn’t load your record"
            body={me.error?.message ?? 'Something went wrong on our side.'}
          />
          <button
            type="button"
            className="btn btn-secondary"
            onClick={me.refresh}
            style={{ ...TAP_TARGET, marginTop: '18px' }}
          >
            Try again
          </button>
        </section>
      </Screen>
    )
  }

  const owner = me.data?.owner ?? null
  // Nothing forces a re-render at the hour boundary, exactly as in the prototype: a page left open
  // from 11:58 still says "Good morning" at 12:01. Not worth a timer.
  const greeting = greetingFor(new Date().getHours())
  const dateLine = dateLineFor(me.data?.serverDate ?? '')

  return (
    <Screen>
      <>
        <header style={HEADER_ROW}>
          <div>
            {dateLine === '' ? null : <div style={KICKER}>{dateLine}</div>}
            <h1 style={PAGE_TITLE}>{`${greeting}, ${firstNameOf(owner?.name ?? null)}`}</h1>
            <p className="text-muted" style={LEAD}>
              {dog === null
                ? 'Everything important about your dog, in one calm place.'
                : `Everything important about ${dog.name}, in one calm place.`}
            </p>
          </div>
        </header>

        {dog === null ? (
          <section className="card elev-sm" style={EMPTY_CARD}>
            <EmptyState
              paths={CARE_ICON_PATHS.bone}
              title="No dog on your record yet"
              body="We can’t find a dog registered to this account. If that looks wrong, we’ll sort it out — it usually means the registration is under a different email address."
              hint="Mention it at your next visit"
            />
          </section>
        ) : (
          <div className="ft-cols">
            <div>
              <Hero dog={dog} onReview={() => go('review')} />
              <ComingUp dog={dog} onSeeAll={() => go('reminders')} />
            </div>
            <div>
              <Today key={dog.dogId} dog={dog} />
            </div>
          </div>
        )}
      </>
    </Screen>
  )
}
